import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useKeycloak } from '../context/KeycloakContext';
import { useAuth } from '../context/AuthContext';
import env from '../config/env';
import processService from '../services/processService';
import api from '../services/apiConfig';
import CodexTypeScopeWarning, { avvisoPerTipo, useMyCodexTypes } from './codex/CodexTypeScopeWarning';

// dataType JSON arriva come 'json': va trattato come oggetto, altrimenti parte come stringa
// e il backend (CodexConverter, case JSON) la scarta senza errore (AUTO-288).
export const toSchemaType = (attribute) => {
  const type = (attribute?.schema?.type || attribute?.dataType || 'string').toLowerCase();
  return type === 'json' ? 'object' : type;
};

const buildDefaultAttributes = (attributes = []) => {
  const defaults = {};
  attributes.forEach((attr) => {
    const schemaType = toSchemaType(attr);
    if (attr.schema && Object.prototype.hasOwnProperty.call(attr.schema, 'default')) {
      const defaultValue = attr.schema.default;
      if (typeof defaultValue === 'object') {
        defaults[attr.code] = JSON.stringify(defaultValue, null, 2);
      } else {
        defaults[attr.code] = defaultValue;
      }
      return;
    }

    switch (schemaType) {
      case 'number':
      case 'integer':
        defaults[attr.code] = '';
        break;
      case 'boolean':
        defaults[attr.code] = false;
        break;
      case 'date':
        defaults[attr.code] = '';
        break;
      case 'array':
        defaults[attr.code] = '[]';
        break;
      case 'object':
        defaults[attr.code] = '{}';
        break;
      default:
        defaults[attr.code] = '';
        break;
    }
  });
  return defaults;
};

const DocumentCreateDialog = ({ open, onClose, onCreated = () => {}, codexTypes = [] }) => {
  const { keycloak } = useKeycloak();
  const { user } = useAuth();

  const defaultOwner = useMemo(() => {
    if (env.useKeycloakAuth) {
      return keycloak?.tokenParsed?.preferred_username || keycloak?.tokenParsed?.email || '';
    }
    return user?.username || user?.email || '';
  }, [keycloak, user]);

  const myCodexTypes = useMyCodexTypes();
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [selectedType, setSelectedType] = useState(null);
  const [processes, setProcesses] = useState([]);
  const [processId, setProcessId] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [owner, setOwner] = useState(defaultOwner);
  const [files, setFiles] = useState([]);
  const [attributeValues, setAttributeValues] = useState({});
  const [attributeErrors, setAttributeErrors] = useState({});
  const [loadingProcesses, setLoadingProcesses] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const resetState = useCallback(() => {
    setSelectedTypeId('');
    setSelectedType(null);
    setProcesses([]);
    setProcessId('');
    setCode(`DOC-${Date.now()}`);
    setDescription('');
    setOwner(defaultOwner);
    setFiles([]);
    setAttributeValues({});
    setAttributeErrors({});
    setLoadingProcesses(false);
    setSubmitting(false);
    setError(null);
  }, [defaultOwner]);

  useEffect(() => {
    if (open) {
      resetState();
    }
  }, [open, resetState]);

  useEffect(() => {
    if (open) {
      setOwner(defaultOwner);
    }
  }, [defaultOwner, open]);

  const handleTypeChange = async (event) => {
    const typeId = event.target.value;
    setSelectedTypeId(typeId);
    setProcesses([]);
    setProcessId('');
    setAttributeErrors({});

    const type = codexTypes.find((item) => item.id === typeId) || null;
    setSelectedType(type);
    setAttributeValues(buildDefaultAttributes(type?.attributes));

    if (typeId) {
      try {
        setLoadingProcesses(true);
        const data = await processService.getProcessesByCodexType(typeId);
        setProcesses(data || []);
        if (data && data.length === 1) {
          setProcessId(data[0].id);
        }
      } catch (procErr) {
        console.error('Unable to fetch processes for codex type', procErr);
        setError('Errore nel caricamento dei processi disponibili.');
      } finally {
        setLoadingProcesses(false);
      }
    }
  };

  const handleFileUpload = (event) => {
    const newFiles = Array.from(event.target.files || []);
    if (newFiles.length) {
      setFiles((prev) => [...prev, ...newFiles]);
    }
  };

  const handleRemoveFile = (index) => {
    setFiles((prev) => prev.filter((_, current) => current !== index));
  };

  const handleAttributeChange = (codeKey, value) => {
    setAttributeValues((prev) => ({
      ...prev,
      [codeKey]: value,
    }));
    setAttributeErrors((prev) => ({
      ...prev,
      [codeKey]: undefined,
    }));
  };

  const renderAttributeField = (attribute) => {
    const schemaType = toSchemaType(attribute);
    const value = attributeValues[attribute.code];
    const label = attribute.isRequired ? `${attribute.description} *` : attribute.description;
    const enumOptions = attribute.schema?.enum;

    if (enumOptions && Array.isArray(enumOptions) && enumOptions.length > 0) {
      return (
        <TextField
          key={attribute.code}
          select
          fullWidth
          label={label}
          value={value ?? ''}
          onChange={(event) => handleAttributeChange(attribute.code, event.target.value)}
          margin="normal"
          helperText={attribute.schema?.description}
        >
          {enumOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    switch (schemaType) {
      case 'boolean':
        return (
          <FormControlLabel
            key={attribute.code}
            control={
              <Switch
                checked={Boolean(value)}
                onChange={(event) => handleAttributeChange(attribute.code, event.target.checked)}
                color="primary"
              />
            }
            label={label}
          />
        );
      case 'number':
      case 'integer':
        return (
          <TextField
            key={attribute.code}
            type="number"
            fullWidth
            label={label}
            margin="normal"
            value={value ?? ''}
            onChange={(event) => {
              const inputValue = event.target.value;
              handleAttributeChange(
                attribute.code,
                inputValue === '' ? '' : Number(inputValue)
              );
            }}
            InputProps={{
              inputProps: {
                min: attribute.schema?.minimum,
                max: attribute.schema?.maximum,
                step: attribute.schema?.multipleOf || 'any',
              },
            }}
            helperText={attribute.schema?.description}
          />
        );
      case 'date':
        return (
          <TextField
            key={attribute.code}
            type="date"
            fullWidth
            label={label}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            value={value || ''}
            onChange={(event) => handleAttributeChange(attribute.code, event.target.value)}
          />
        );
      case 'object':
        return (
          <TextField
            key={attribute.code}
            multiline
            fullWidth
            minRows={4}
            label={label}
            margin="normal"
            value={value ?? ''}
            onChange={(event) => handleAttributeChange(attribute.code, event.target.value)}
            error={Boolean(attributeErrors[attribute.code])}
            helperText={attributeErrors[attribute.code] || attribute.schema?.description || 'Inserisci JSON valido'}
          />
        );
      default:
        return (
          <TextField
            key={attribute.code}
            fullWidth
            label={label}
            margin="normal"
            value={value ?? ''}
            onChange={(event) => handleAttributeChange(attribute.code, event.target.value)}
            helperText={attribute.schema?.description}
          />
        );
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!selectedTypeId) {
      setError('Seleziona un tipo di documento.');
      return false;
    }
    if (!code.trim()) {
      setError('Inserisci un codice documento.');
      return false;
    }
    if (!owner.trim()) {
      setError('Inserisci un proprietario.');
      return false;
    }
    if (files.length === 0) {
      setError('Carica almeno un file.');
      return false;
    }

    (selectedType?.attributes || []).forEach((attribute) => {
      if (!attribute.isRequired) return;
      const value = attributeValues[attribute.code];
      if (value === undefined || value === null || value === '') {
        newErrors[attribute.code] = 'Campo obbligatorio';
      }
    });

    setAttributeErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      setError('Compila tutti i campi obbligatori.');
      return false;
    }

    setError(null);
    return true;
  };

  const prepareAttributesForSubmit = () => {
    const prepared = {};
    const parseErrors = {};

    (selectedType?.attributes || []).forEach((attribute) => {
      const schemaType = toSchemaType(attribute);
      const rawValue = attributeValues[attribute.code];

      if (schemaType === 'boolean') {
        prepared[attribute.code] = Boolean(rawValue);
        return;
      }

      if (schemaType === 'number' || schemaType === 'integer') {
        if (rawValue === '' || rawValue === null || rawValue === undefined) {
          prepared[attribute.code] = rawValue;
        } else {
          prepared[attribute.code] = Number(rawValue);
        }
        return;
      }

      if (schemaType === 'object' || schemaType === 'array') {
        if (!rawValue) {
          prepared[attribute.code] = schemaType === 'array' ? [] : {};
          return;
        }
        try {
          const parsed = JSON.parse(rawValue);
          // il backend salva un attributo JSON solo come oggetto (AUTO-299)
          if (schemaType === 'object' && (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed))) {
            parseErrors[attribute.code] = 'Serve un oggetto JSON, es. {"campo": "valore"}';
            return;
          }
          prepared[attribute.code] = parsed;
        } catch (parseError) {
          parseErrors[attribute.code] = 'JSON non valido';
        }
        return;
      }

      prepared[attribute.code] = rawValue ?? '';
    });

    return { prepared, parseErrors };
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    const { prepared, parseErrors } = prepareAttributesForSubmit();
    if (Object.keys(parseErrors).length > 0) {
      setAttributeErrors(parseErrors);
      setError('Correggi gli errori negli attributi.');
      return;
    }

    setSubmitting(true);

    const codexPayload = {
      codexType: selectedTypeId,
      code: code.trim(),
      description: description.trim(),
      owner: owner.trim(),
      attributeValues: prepared,
    };

    let createdCodex;
    try {
      const response = await api.post('/codexes', codexPayload);
      createdCodex = response.data;
    } catch (creationError) {
      console.error('Errore nella creazione del codex', creationError);
      const serverMessage = creationError?.response?.status === 400 && creationError.response.data?.message;
      setError(serverMessage || 'Impossibile creare il documento.');
      setSubmitting(false);
      return;
    }

    try {
      await Promise.all(
        files.map(async (file) => {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('codexId', createdCodex.id);
          await api.post('/codex-files', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        })
      );
    } catch (uploadError) {
      console.error('Errore durante il caricamento dei file', uploadError);
      setError('Documento creato senza allegati: carica nuovamente i file dal dettaglio.');
    }

    if (processId) {
      try {
        await processService.startCodexProcess(createdCodex.id, processId);
      } catch (processError) {
        console.error('Errore nell\'avvio del processo', processError);
      }
    }

    setSubmitting(false);
    onCreated?.(createdCodex);
  };

  const disableActions = submitting;

  return (
    <Dialog open={open} onClose={disableActions ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Nuovo Documento</DialogTitle>
      {submitting && <LinearProgress />}
      <DialogContent dividers>
        <Stack spacing={3}>
          {error && <Alert severity="error">{error}</Alert>}

          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Informazioni generali
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={selectedTypeId}
                    label="Tipo"
                    onChange={handleTypeChange}
                    disabled={disableActions}
                  >
                    <MenuItem value="">
                      <em>Seleziona un tipo</em>
                    </MenuItem>
                    {[...(codexTypes || [])]
                      .sort((a, b) => (a.code || '').localeCompare(b.code || ''))
                      .map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.code ? `${type.code} - ${type.description || ''}`.trim() : (type.description || '')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {/* D2: si carica anche cio' che non si vedra', ma non in silenzio. Occupa
                  tutta la riga e non la cella del selettore, perche' e' una frase da
                  leggere e non un helper text da scorrere. */}
              {avvisoPerTipo(selectedTypeId, myCodexTypes) && (
                <Grid item xs={12}>
                  <CodexTypeScopeWarning
                    codexTypeId={selectedTypeId}
                    myCodexTypes={myCodexTypes}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Processo"
                  select
                  fullWidth
                  size="small"
                  value={processId}
                  onChange={(event) => setProcessId(event.target.value)}
                  disabled={disableActions || loadingProcesses || processes.length === 0}
                  helperText={loadingProcesses ? 'Caricamento processi...' : processes.length === 0 ? 'Nessun processo disponibile' : 'Processo da avviare dopo la creazione'}
                >
                  <MenuItem value="">
                    <em>Non avviare processo</em>
                  </MenuItem>
                  {processes.map((process) => (
                    <MenuItem key={process.id} value={process.id}>
                      {process.code || process.id}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Codice"
                  fullWidth
                  size="small"
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  disabled={disableActions}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Proprietario"
                  fullWidth
                  size="small"
                  value={owner}
                  onChange={(event) => setOwner(event.target.value)}
                  disabled={disableActions}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Descrizione"
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  disabled={disableActions}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Allegati
            </Typography>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
              disabled={disableActions}
            >
              Carica file
              <input type="file" hidden multiple onChange={handleFileUpload} />
            </Button>
            <Stack direction="row" spacing={1} mt={2} flexWrap="wrap">
              {files.map((file, index) => (
                <Chip
                  key={`${file.name}-${index}`}
                  label={`${file.name} (${(file.size / 1024).toFixed(1)} KB)`}
                  onDelete={disableActions ? undefined : () => handleRemoveFile(index)}
                  deleteIcon={<DeleteIcon />}
                  variant="outlined"
                  sx={{ mr: 1, mb: 1 }}
                />
              ))}
            </Stack>
          </Box>

          {selectedType?.attributes?.length ? (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Attributi
              </Typography>
              {selectedType.attributes.map((attribute) => (
                <Box key={attribute.id || attribute.code}>{renderAttributeField(attribute)}</Box>
              ))}
            </Box>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={disableActions}>
          Annulla
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={disableActions || !selectedTypeId}>
          Crea documento
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DocumentCreateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  codexTypes: PropTypes.arrayOf(PropTypes.object)
};

export default DocumentCreateDialog;
